import React, { memo, useState, useCallback, useEffect } from 'react';
import { DatePicker, createUseStyles } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { formatDate, parseDateWithFormat } from 'lib/dates';

import {
    applyLineSmartFieldOverridesToInputParams,
    getLineSmartFieldOverrides,
    resolveCellIsEnriched,
} from './applyLineSmartFieldOverrides';
import styles from './styles';

const useStyles = createUseStyles(styles, 'DatePickerCell');

const isEmptyValue = (value) => value === null || value === undefined || value === '';

const resolveDate = (value, defaultValue, locale) => {
    const resolvedValue = isEmptyValue(value) ? defaultValue : value;
    return parseDateWithFormat(resolvedValue, locale, 'P') || '';
};

const DatePickerCell = memo((params) => {
    const { language, locale } = useWeb5Context();
    const [date, setDate] = useState(() =>
        resolveDate(params.value, params.inputParams?.defaultValue, locale),
    );
    const classes = useStyles();

    const field = params.colDef.field;
    const lineOverrides = getLineSmartFieldOverrides(params.data, field);
    const resolvedInputParams = applyLineSmartFieldOverridesToInputParams(
        params.inputParams,
        lineOverrides,
    );
    const { onChange, isEnriched: isEnrichedParam, ...inputParams } = resolvedInputParams || {};
    const isReadOnly = !!inputParams.isReadOnly;
    const isEnriched = resolveCellIsEnriched(
        { ...inputParams, isEnriched: isEnrichedParam },
        params.data,
        field,
        isReadOnly,
    );

    useEffect(() => {
        setDate(resolveDate(params.value, inputParams.defaultValue, locale));
    }, [inputParams.defaultValue, locale, params.value]);

    const handleOnChange = useCallback(
        (value) => {
            if (isReadOnly) return;
            const id = params.data.id || params.data.Id || params.data.ID || '';
            onChange({ rowIndex: params.node.rowIndex, id, field, value });
            setDate(value);
        },
        [field, isReadOnly, onChange, params],
    );

    const format = useCallback(
        (value) => {
            return formatDate(value, locale, 'P');
        },
        [locale],
    );

    if (!params.inputParams) return null;

    if (params.CustomCell) {
        const CustomCell = params.CustomCell;
        return (
            <CustomCell
                {...inputParams}
                value={date}
                onChange={handleOnChange}
                isEnriched={isEnriched}
            />
        );
    }

    return (
        <div className={['w5-cell', classes.editableCell].join(' ')}>
            <DatePicker
                {...inputParams}
                value={date}
                onChange={handleOnChange}
                lang={language}
                formatDate={format}
                isFullWidth
                isEnriched={isEnriched}
            />
        </div>
    );
});

export default DatePickerCell;
